import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  PlayCircle, FileText, BookOpen, Search, ExternalLink, 
  Clock, Star, Filter, ChevronRight, Youtube, Globe,
  BookMarked, Newspaper, Video, Library, Lightbulb,
  GraduationCap, TrendingUp, Award, Bookmark, BookmarkCheck
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function ResourcesHub({ enrolledCourses = [], token }) {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savedResources, setSavedResources] = useState([]);

  // Generate curated resources based on enrolled courses
  useEffect(() => {
    const generateResources = () => {
      setLoading(true);
      
      // Create resources based on enrolled courses
      const generatedResources = [];
      
      enrolledCourses.forEach(course => {
        const courseQuery = encodeURIComponent(course.title);
        
        // Videos for each course - YouTube search URLs
        generatedResources.push({
          id: `video-${course.id}-1`,
          type: 'video',
          title: `${course.title} - Complete Course Overview`,
          description: `A comprehensive video introduction to ${course.title}. Perfect for beginners and those looking to refresh their knowledge.`,
          source: 'YouTube',
          duration: '45 min',
          courseId: course.id,
          courseName: course.title,
          url: `https://www.youtube.com/results?search_query=${courseQuery}+tutorial+course`,
          thumbnail: `https://img.youtube.com/vi/dQw4w9WgXcQ/maxresdefault.jpg`,
          rating: 4.8,
          views: '125K'
        });
        
        generatedResources.push({
          id: `video-${course.id}-2`,
          type: 'video',
          title: `Advanced ${course.title} Techniques`,
          description: `Deep dive into advanced concepts and real-world applications in ${course.title}.`,
          source: 'YouTube',
          duration: '1h 20min',
          courseId: course.id,
          courseName: course.title,
          url: `https://www.youtube.com/results?search_query=${courseQuery}+advanced+tutorial`,
          thumbnail: `https://img.youtube.com/vi/dQw4w9WgXcQ/maxresdefault.jpg`,
          rating: 4.9,
          views: '89K'
        });
        
        // Articles for each course - Google Scholar / Medium search
        generatedResources.push({
          id: `article-${course.id}-1`,
          type: 'article',
          title: `Understanding the Fundamentals of ${course.title}`,
          description: `A well-structured article covering the core principles and foundational concepts you need to master.`,
          source: 'Medium',
          readTime: '12 min read',
          courseId: course.id,
          courseName: course.title,
          url: `https://medium.com/search?q=${courseQuery}`,
          author: 'Expert Author',
          rating: 4.7
        });
        
        generatedResources.push({
          id: `article-${course.id}-2`,
          type: 'article',
          title: `Best Practices in ${course.title}`,
          description: `Industry-standard practices and tips from professionals working in the field.`,
          source: 'Google Scholar',
          readTime: '8 min read',
          courseId: course.id,
          courseName: course.title,
          url: `https://scholar.google.com/scholar?q=${courseQuery}`,
          author: 'Industry Professional',
          rating: 4.6
        });
        
        // Books for each course - Google Books / Amazon search
        generatedResources.push({
          id: `book-${course.id}-1`,
          type: 'book',
          title: `The Complete Guide to ${course.title}`,
          description: `A comprehensive textbook covering everything from basics to advanced topics. Highly recommended by educators.`,
          author: 'Academic Press',
          pages: 450,
          courseId: course.id,
          courseName: course.title,
          url: `https://www.google.com/search?tbm=bks&q=${courseQuery}+textbook`,
          rating: 4.9,
          format: 'PDF / Hardcover'
        });
        
        generatedResources.push({
          id: `book-${course.id}-2`,
          type: 'book',
          title: `${course.title} in Practice`,
          description: `Hands-on exercises and real-world case studies to solidify your understanding.`,
          author: 'O\'Reilly Media',
          pages: 320,
          courseId: course.id,
          courseName: course.title,
          url: `https://www.amazon.com/s?k=${courseQuery}+book`,
          rating: 4.8,
          format: 'eBook / Print'
        });
      });
      
      // Add some general learning resources
      generatedResources.push({
        id: 'general-video-1',
        type: 'video',
        title: 'How to Study Effectively - Science-Based Techniques',
        description: 'Evidence-based study techniques that will help you learn faster and retain information longer.',
        source: 'YouTube',
        duration: '18 min',
        courseId: 'general',
        courseName: 'Study Skills',
        url: 'https://www.youtube.com/results?search_query=how+to+study+effectively+science+based',
        rating: 4.9,
        views: '2.1M'
      });
      
      generatedResources.push({
        id: 'general-article-1',
        type: 'article',
        title: 'The Feynman Technique: The Best Way to Learn Anything',
        description: 'Learn how Nobel Prize-winning physicist Richard Feynman approached learning complex topics.',
        source: 'Farnam Street',
        readTime: '6 min read',
        courseId: 'general',
        courseName: 'Learning Strategies',
        url: 'https://fs.blog/feynman-technique/',
        rating: 4.8
      });
      
      setResources(generatedResources);
      setLoading(false);
    };
    
    generateResources();
  }, [enrolledCourses]);

  // Filter resources based on category and search
  const filteredResources = resources.filter(resource => {
    const matchesCategory = activeCategory === 'all' || resource.type === activeCategory;
    const matchesSearch = searchQuery === '' || 
      resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      resource.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      resource.courseName.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleSaveResource = (resourceId) => {
    if (savedResources.includes(resourceId)) {
      setSavedResources(prev => prev.filter(id => id !== resourceId));
      toast.success('Resource removed from saved');
    } else {
      setSavedResources(prev => [...prev, resourceId]);
      toast.success('Resource saved for later');
    }
  };

  const getCategoryIcon = (type) => {
    switch (type) {
      case 'video': return <Video className="w-4 h-4" />;
      case 'article': return <Newspaper className="w-4 h-4" />;
      case 'book': return <BookOpen className="w-4 h-4" />;
      default: return <Library className="w-4 h-4" />;
    }
  };

  const getCategoryColor = (type) => {
    switch (type) {
      case 'video': return 'bg-red-100 text-red-700 border-red-200';
      case 'article': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'book': return 'bg-amber-100 text-amber-700 border-amber-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const ResourceCard = ({ resource }) => {
    const isSaved = savedResources.includes(resource.id);
    
    const handleOpenResource = () => {
      window.open(resource.url, '_blank', 'noopener,noreferrer');
    };
    
    return (
      <Card className="group hover:shadow-lg transition-all duration-300 border-slate-200 overflow-hidden">
        {resource.type === 'video' && resource.thumbnail && (
          <div 
            className="relative h-40 bg-slate-100 overflow-hidden cursor-pointer"
            onClick={handleOpenResource}
          >
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent z-10" />
            <div className="absolute inset-0 flex items-center justify-center z-20">
              <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                <PlayCircle className="w-8 h-8 text-red-600" />
              </div>
            </div>
            <div className="absolute bottom-2 right-2 z-20">
              <Badge className="bg-black/70 text-white text-xs">{resource.duration}</Badge>
            </div>
            <img 
              src={resource.thumbnail} 
              alt={resource.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
        )}
        
        <CardContent className={`p-4 ${resource.type !== 'video' ? 'pt-4' : ''}`}>
          <div className="flex items-start justify-between gap-2 mb-2">
            <Badge className={`${getCategoryColor(resource.type)} text-xs font-medium flex items-center gap-1`}>
              {getCategoryIcon(resource.type)}
              {resource.type.charAt(0).toUpperCase() + resource.type.slice(1)}
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              className={`h-8 w-8 p-0 ${isSaved ? 'text-slate-900' : 'text-slate-400 hover:text-slate-900'}`}
              onClick={() => toggleSaveResource(resource.id)}
            >
              {isSaved ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
            </Button>
          </div>
          
          <h3 
            className="font-semibold text-slate-900 mb-1 line-clamp-2 group-hover:text-slate-700 transition-colors cursor-pointer"
            onClick={handleOpenResource}
          >
            {resource.title}
          </h3>
          
          <p className="text-sm text-slate-600 mb-3 line-clamp-2">
            {resource.description}
          </p>
          
          <div className="flex items-center justify-between text-xs text-slate-500 mb-3">
            <div className="flex items-center gap-2">
              {resource.source && (
                <span className="flex items-center gap-1">
                  <Globe className="w-3 h-3" />
                  {resource.source}
                </span>
              )}
              {resource.readTime && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {resource.readTime}
                </span>
              )}
              {resource.pages && (
                <span className="flex items-center gap-1">
                  <FileText className="w-3 h-3" />
                  {resource.pages} pages
                </span>
              )}
              {resource.views && (
                <span className="flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  {resource.views} views
                </span>
              )}
            </div>
            {resource.rating && (
              <span className="flex items-center gap-1 text-amber-600">
                <Star className="w-3 h-3 fill-amber-400" />
                {resource.rating}
              </span>
            )}
          </div>
          
          <div className="flex items-center justify-between pt-3 border-t border-slate-100">
            <Badge variant="outline" className="text-xs bg-slate-50">
              <GraduationCap className="w-3 h-3 mr-1" />
              {resource.courseName}
            </Badge>
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-slate-700 hover:text-slate-900 hover:bg-slate-100 text-xs h-7 px-2"
              onClick={() => window.open(resource.url, '_blank', 'noopener,noreferrer')}
            >
              Open Resource
              <ExternalLink className="w-3 h-3 ml-1" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  const stats = {
    videos: resources.filter(r => r.type === 'video').length,
    articles: resources.filter(r => r.type === 'article').length,
    books: resources.filter(r => r.type === 'book').length,
    saved: savedResources.length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading resources...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
            <Library className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">Resources Hub</h2>
            <p className="text-slate-300">Curated learning materials for your courses</p>
          </div>
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mt-6">
          <div className="bg-white/10 rounded-xl p-3 text-center backdrop-blur-sm">
            <Video className="w-5 h-5 mx-auto mb-1 text-red-400" />
            <p className="text-2xl font-bold">{stats.videos}</p>
            <p className="text-xs text-slate-300">Videos</p>
          </div>
          <div className="bg-white/10 rounded-xl p-3 text-center backdrop-blur-sm">
            <Newspaper className="w-5 h-5 mx-auto mb-1 text-blue-400" />
            <p className="text-2xl font-bold">{stats.articles}</p>
            <p className="text-xs text-slate-300">Articles</p>
          </div>
          <div className="bg-white/10 rounded-xl p-3 text-center backdrop-blur-sm">
            <BookOpen className="w-5 h-5 mx-auto mb-1 text-amber-400" />
            <p className="text-2xl font-bold">{stats.books}</p>
            <p className="text-xs text-slate-300">Books</p>
          </div>
          <div className="bg-white/10 rounded-xl p-3 text-center backdrop-blur-sm">
            <BookmarkCheck className="w-5 h-5 mx-auto mb-1 text-emerald-400" />
            <p className="text-2xl font-bold">{stats.saved}</p>
            <p className="text-xs text-slate-300">Saved</p>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search videos, articles, books..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant={activeCategory === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveCategory('all')}
                className={activeCategory === 'all' ? 'bg-slate-900 hover:bg-slate-800' : ''}
              >
                <Library className="w-4 h-4 mr-1" />
                All
              </Button>
              <Button
                variant={activeCategory === 'video' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveCategory('video')}
                className={activeCategory === 'video' ? 'bg-red-600 hover:bg-red-700' : ''}
              >
                <Video className="w-4 h-4 mr-1" />
                Videos
              </Button>
              <Button
                variant={activeCategory === 'article' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveCategory('article')}
                className={activeCategory === 'article' ? 'bg-blue-600 hover:bg-blue-700' : ''}
              >
                <Newspaper className="w-4 h-4 mr-1" />
                Articles
              </Button>
              <Button
                variant={activeCategory === 'book' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveCategory('book')}
                className={activeCategory === 'book' ? 'bg-amber-600 hover:bg-amber-700' : ''}
              >
                <BookOpen className="w-4 h-4 mr-1" />
                Books
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Resources Grid */}
      {filteredResources.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No resources found</h3>
            <p className="text-slate-600 mb-4">
              {searchQuery 
                ? `No results for "${searchQuery}". Try a different search term.`
                : 'Enroll in courses to see related learning resources.'}
            </p>
            {searchQuery && (
              <Button variant="outline" onClick={() => setSearchQuery('')}>
                Clear Search
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredResources.map(resource => (
            <ResourceCard key={resource.id} resource={resource} />
          ))}
        </div>
      )}

      {/* Quick Tips Section */}
      <Card className="border-slate-200 bg-slate-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-slate-200 rounded-lg flex items-center justify-center flex-shrink-0">
              <Lightbulb className="w-5 h-5 text-slate-700" />
            </div>
            <div>
              <h4 className="font-semibold text-slate-900 mb-1">Learning Tip</h4>
              <p className="text-sm text-slate-600">
                Combine different resource types for better retention. Watch a video to get an overview, 
                read articles for deeper understanding, and use books as comprehensive references.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
